import type {
  CourseNode,
  CourseNodeWithStatus,
  NodeStatus,
  UserNodeProgress,
} from "@/types/courses";

/**
 * Pure function that merges raw course nodes with user progress
 * and content-step counts to produce CourseNodeWithStatus[].
 *
 * Prerequisite logic:
 *  - If a node has progress → use its status (completed / in_progress)
 *  - Otherwise check prerequisites:
 *      • null / empty → "available"
 *      • all prereqs completed → "available"
 *      • else → "locked"
 */
export function computeNodeStates(
  nodes: CourseNode[],
  nodeProgressMap: Map<string, UserNodeProgress>,
  contentStepCounts: Map<string, number>,
): CourseNodeWithStatus[] {
  // First pass: determine raw status for each node so we can resolve prereqs
  const statusMap = new Map<string, NodeStatus>();

  for (const node of nodes) {
    const progress = nodeProgressMap.get(node.id);
    if (progress) {
      if (progress.status === "completed") {
        statusMap.set(node.id, "completed");
      } else if (progress.status === "in_progress") {
        statusMap.set(node.id, "in_progress");
      } else {
        // not_started with a progress row → treat as available
        statusMap.set(node.id, "available");
      }
    } else {
      // Will resolve in second pass based on prerequisites
      statusMap.set(node.id, "locked");
    }
  }

  // Second pass: resolve "locked" nodes whose prerequisites are all completed
  for (const node of nodes) {
    if (statusMap.get(node.id) !== "locked") continue;

    const prereqs = node.prerequisites;
    if (!prereqs || prereqs.length === 0) {
      statusMap.set(node.id, "available");
    } else {
      const allPrereqsMet = prereqs.every(
        (pid) => statusMap.get(pid) === "completed",
      );
      if (allPrereqsMet) {
        statusMap.set(node.id, "available");
      }
    }
  }

  // Build final array
  return nodes.map((node): CourseNodeWithStatus => {
    const status = statusMap.get(node.id) ?? "locked";
    const progress = nodeProgressMap.get(node.id);
    const totalSteps = contentStepCounts.get(node.id) ?? 0;
    const currentStep = progress?.currentStepIndex ?? 0;

    return {
      ...node,
      status,
      progress: status === "completed" ? 1 : totalSteps > 0 ? currentStep / totalSteps : 0,
      xpEarned: progress?.xpEarned ?? 0,
    };
  });
}
